import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
    Alert,
    Button,
    Card,
    DatePicker,
    Empty,
    Form,
    Input,
    Select,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import { NoPermission } from '../../../components/NoPermission';
import { ResourceCode } from '../../../constants/resourceCodes';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import { SiteType } from '../../../graphql/types/SiteType';
import { hasResource } from '../../../utils/resourceAuth';
import {
    GET_TIKTOK_EXCEPTION_REPORT_ORDERS,
    GetTikTokExceptionReportOrdersResponse,
    GetTikTokExceptionReportOrdersVariables,
    TikTokExceptionReportOrderItem,
} from './graphql/getTikTokExceptionReportOrders';
import {
    buildTikTokExceptionOrderVariables,
    canQueryTikTokExceptionOrders,
    createDefaultTikTokExceptionOrderFilters,
    DEFAULT_PAGE_SIZE,
    getSafeHttpUrl,
    MAX_TRACKING_NUMBER_FILTER_COUNT,
    openSafeHttpUrl,
    TikTokExceptionOrderFilterState,
} from './ttExceptionOrderUtils';

const STATUS_COLORS: Record<number, string> = {
    0: 'default',
    10: 'processing',
    20: 'blue',
    30: 'success',
    40: 'default',
};

const TikTokExceptionReportOrderPage: React.FC = () => {
    const { t } = useTranslation();
    const { currentWarehouse } = useWarehouseContext();
    const permitted = hasResource([ResourceCode.menu.HUB_TT_EXCEPTION_ORDER_QUERY]);
    const queryEnabled = canQueryTikTokExceptionOrders(permitted, currentWarehouse);
    const warehouseId = Number(currentWarehouse?.id ?? 0);

    const [draftFilters, setDraftFilters] = useState<TikTokExceptionOrderFilterState>(
        createDefaultTikTokExceptionOrderFilters,
    );
    const [appliedFilters, setAppliedFilters] = useState<TikTokExceptionOrderFilterState>(
        createDefaultTikTokExceptionOrderFilters,
    );
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    useEffect(() => {
        const defaults = createDefaultTikTokExceptionOrderFilters();
        setDraftFilters(defaults);
        setAppliedFilters(defaults);
        setPage(1);
    }, [currentWarehouse?.id]);

    const variables = useMemo<GetTikTokExceptionReportOrdersVariables>(
        () => buildTikTokExceptionOrderVariables(warehouseId, appliedFilters, page, pageSize),
        [warehouseId, appliedFilters, page, pageSize],
    );

    const { data, loading, error } = useQuery<
        GetTikTokExceptionReportOrdersResponse,
        GetTikTokExceptionReportOrdersVariables
    >(GET_TIKTOK_EXCEPTION_REPORT_ORDERS, {
        variables,
        skip: !queryEnabled,
        fetchPolicy: 'cache-and-network',
    });

    const content = data?.getTikTokExceptionReportOrders.content ?? [];
    const total = Number(data?.getTikTokExceptionReportOrders.totalElements ?? 0);
    const reasonOptions = useMemo(() => [
        { value: '', label: t('wms.ttExceptionOrders.filters.exceptionReasonAll') },
        { value: 'Damaged', label: 'Damaged' },
        { value: 'Unfulfillable', label: 'Unfulfillable' },
        { value: 'Unscannable', label: 'Unscannable' },
    ], [t]);

    const columns = useMemo<ColumnsType<TikTokExceptionReportOrderItem>>(() => [
        {
            title: t('wms.ttExceptionOrders.columns.hub'),
            dataIndex: 'warehouseId',
            width: 180,
            render: (value: string) => `${currentWarehouse?.name ?? '-'} (${value})`,
        },
        {
            title: t('wms.ttExceptionOrders.columns.trackingNumber'),
            dataIndex: 'trackingNumber',
            width: 190,
        },
        {
            title: t('wms.ttExceptionOrders.columns.exceptionReason'),
            dataIndex: 'exceptionReasonCode',
            width: 160,
        },
        {
            title: t('wms.ttExceptionOrders.columns.exceptionSubReason'),
            dataIndex: 'exceptionSubReasonCode',
            width: 180,
            render: (value: string | null) => value || '-',
        },
        {
            title: t('wms.ttExceptionOrders.columns.status'),
            dataIndex: 'status',
            width: 120,
            render: (value: number) => (
                <Tag color={STATUS_COLORS[value] ?? 'default'}>
                    {t(`wms.ttExceptionOrders.status.${value}`, { defaultValue: String(value) })}
                </Tag>
            ),
        },
        {
            title: t('wms.ttExceptionOrders.columns.action'),
            dataIndex: 'action',
            width: 120,
            render: (value: string | null) => value || '-',
        },
        {
            title: t('wms.ttExceptionOrders.columns.externalExceptionId'),
            dataIndex: 'externalExceptionId',
            width: 180,
            render: (value: string | null) => value || '-',
        },
        {
            title: t('wms.ttExceptionOrders.columns.awbUrl'),
            dataIndex: 'awbUrl',
            width: 150,
            render: (value: string | null) => {
                const safeUrl = getSafeHttpUrl(value);
                if (!safeUrl) return '-';
                return (
                    <Space size={4}>
                        <Button type="link" className="p-0" onClick={() => openSafeHttpUrl(safeUrl)}>
                            {t('wms.ttExceptionOrders.actions.openAwb')}
                        </Button>
                        <Tooltip title={t('wms.ttExceptionOrders.actions.copyAwb')}>
                            <Typography.Text copyable={{ text: safeUrl }} />
                        </Tooltip>
                    </Space>
                );
            },
        },
        {
            title: t('wms.ttExceptionOrders.columns.createdAt'),
            dataIndex: 'createdAt',
            width: 180,
            render: (value: number) => formatEpochSeconds(value),
        },
        {
            title: t('wms.ttExceptionOrders.columns.callbackAt'),
            dataIndex: 'callbackAt',
            width: 180,
            render: (value: number | null) => value ? formatEpochSeconds(value) : '-',
        },
    ], [currentWarehouse?.name, t]);

    const handleSearch = () => {
        setAppliedFilters({
            ...draftFilters,
            trackingNumbers: Array.from(new Set(
                draftFilters.trackingNumbers.map((value) => value.trim()).filter(Boolean),
            )),
            exceptionReasonCode: draftFilters.exceptionReasonCode.trim(),
        });
        setPage(1);
    };

    const handleReset = () => {
        const defaults = createDefaultTikTokExceptionOrderFilters();
        setDraftFilters(defaults);
        setAppliedFilters(defaults);
        setPage(1);
    };

    const handleTableChange = (pagination: TablePaginationConfig) => {
        const nextSize = pagination.pageSize ?? DEFAULT_PAGE_SIZE;
        setPageSize(nextSize);
        setPage(nextSize === pageSize ? pagination.current ?? 1 : 1);
    };

    if (!permitted) return <NoPermission />;

    if (currentWarehouse?.siteType !== SiteType.HUB) {
        return (
            <Card>
                <Empty description={t('wms.ttExceptionOrders.messages.selectHub')} />
            </Card>
        );
    }

    return (
        <Card title={t('wms.ttExceptionOrders.title')}>
            <Form layout="vertical">
                <Space wrap align="end" size="middle">
                    <Form.Item label={t('wms.ttExceptionOrders.filters.hub')}>
                        <Input value={`${currentWarehouse.name ?? '-'} (${currentWarehouse.id})`} disabled />
                    </Form.Item>
                    <Form.Item label={t('wms.ttExceptionOrders.filters.createdAt')} required>
                        <DatePicker.RangePicker
                            showTime
                            allowClear={false}
                            value={draftFilters.createdAtRange}
                            onChange={(range) => {
                                if (range?.[0] && range[1]) {
                                    setDraftFilters((previous) => ({
                                        ...previous,
                                        createdAtRange: range as [Dayjs, Dayjs],
                                    }));
                                }
                            }}
                        />
                    </Form.Item>
                    <Form.Item label={t('wms.ttExceptionOrders.filters.trackingNumber')}>
                        <Select
                            mode="tags"
                            className="w-[480px] max-w-full"
                            maxCount={MAX_TRACKING_NUMBER_FILTER_COUNT}
                            maxTagCount={1}
                            open={false}
                            tokenSeparators={[',', ' ', '\n']}
                            value={draftFilters.trackingNumbers}
                            placeholder={String(t('wms.ttExceptionOrders.filters.trackingNumberPlaceholder'))}
                            onChange={(values) => setDraftFilters((previous) => ({
                                ...previous,
                                trackingNumbers: values,
                            }))}
                        />
                    </Form.Item>
                    <Form.Item label={t('wms.ttExceptionOrders.filters.exceptionReason')}>
                        <Select
                            className="min-w-[220px]"
                            value={draftFilters.exceptionReasonCode}
                            options={reasonOptions}
                            onChange={(value) => setDraftFilters((previous) => ({
                                ...previous,
                                exceptionReasonCode: value,
                            }))}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" onClick={handleSearch}>
                                {t('common.search')}
                            </Button>
                            <Button onClick={handleReset}>{t('common.reset')}</Button>
                        </Space>
                    </Form.Item>
                </Space>
            </Form>

            {error && (
                <Alert
                    className="mb-4"
                    type="error"
                    showIcon
                    message={t('wms.ttExceptionOrders.messages.queryFailed')}
                    description={error.message}
                />
            )}

            <Table<TikTokExceptionReportOrderItem>
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={content}
                scroll={{ x: 1600 }}
                pagination={{
                    current: page,
                    pageSize,
                    total,
                    showSizeChanger: true,
                    pageSizeOptions: [20, 50, 100],
                    showTotal: (count) => t('wms.ttExceptionOrders.paginationTotal', { total: count }),
                }}
                onChange={handleTableChange}
            />
        </Card>
    );
};

const formatEpochSeconds = (value: number): string => {
    const milliseconds = Number(value) * 1000;
    if (!Number.isFinite(milliseconds)) return '-';
    return new Date(milliseconds).toLocaleString();
};

export default TikTokExceptionReportOrderPage;
